/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 08 2024
 **/

import { useFileManager } from "@/frameworks/core/hooks";
import { usePersistUser } from "@/frameworks/core/persistence";
import { Row, Typography } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

export default function FileUploader({
	file
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
}) {
	const fileUploader = usePersistUser(file.createBy || "");
	const { currentFolder } = useFileManager();
	return (
		<Row gap={6} css={{ width: "100%" }}>
			<ChannelName file={file} />
			{!currentFolder.isMyFile && (
				<Typography typoSize="Body5" typoColor="neutral_50_100">
					•
				</Typography>
			)}
			{!currentFolder.isMyFile && (
				<Typography
					ellipsis
					maxLine={1}
					typoSize="Body5"
					typoColor={currentFolder.isMyFile ? "neutral_80_100" : "neutral_50_100"}
				>
					{fileUploader?.name || ""}
				</Typography>
			)}
		</Row>
	);
}

function ChannelName({ file }: { file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number] }) {
	if (file.referenceType === "SPACE") {
		return (
			<Typography typoSize="Body5" typoColor="neutral_80_100">
				{file.referenceName}
			</Typography>
		);
	}

	if (file?.space) {
		return (
			<Typography typoSize="Body5" typoColor="neutral_80_100">
				{file.space.spaceName}
			</Typography>
		);
	}

	if (file.chatRoom?.dm && file.chatRoom.dm.users.length > 1) {
		return (
			<Typography typoSize="Body5" typoColor="neutral_80_100">
				{file.chatRoom.dm.users
					.filter((v) => v.id !== Configure.userID)
					.map((v) => v.name)
					.join(", ")}
			</Typography>
		);
	}

	if (file.referenceType === "INDIVIDUAL") {
		return (
			<Typography typoSize="Body5" typoColor="neutral_80_100">
				{_T("마이룸")}
			</Typography>
		);
	}

	if (file.referenceType === "CHAT") {
		return (
			<Typography typoSize="Body5" typoColor="neutral_80_100" ellipsis maxLine={1}>
				{file.user?.name}
			</Typography>
		);
	}
}
